import { useEffect, useState } from "react";
import { listAgents, deleteAgent } from "../api/agentApi";
import { Link } from "react-router-dom";
import { useUser } from "./UserContext";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Toolbar,
  Alert,
  Divider
} from "@mui/material";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

export default function AgentListPage() {
  const [agents, setAgents] = useState([]);
  const [deleting, setDeleting] = useState("");
  const [error, setError] = useState("");
  const { user, logout } = useUser();

  useEffect(() => {
    listAgents().then(data => setAgents(data.agents || []));
  }, []);

  const handleDelete = async (agentId) => {
    if (!window.confirm(`Delete agent '${agentId}'? This cannot be undone.`)) return;
    setDeleting(agentId);
    try {
      await deleteAgent(agentId);
      setAgents(agents.filter(a => a.agent_id !== agentId));
    } catch (e) {
      setError("Failed to delete agent.");
    }
    setDeleting("");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Toolbar disableGutters sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4" sx={{ pl: 0 }}>Agents</Typography>
      </Toolbar>
      <Divider sx={{ mb: 2 }} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Agent ID</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Agent Name</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem", width: 480 }}>Agent Description</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Owner</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agents.map(agent => (
              <TableRow key={agent.agent_id} hover sx={{ fontSize: "0.95rem" }}>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  <Link to={`/agent/${agent.agent_id}`} style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}>
                    {agent.agent_id}
                  </Link>
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>{agent.name}</TableCell>
                <TableCell sx={{
                  fontSize: "0.95rem",
                  py: 1,
                  maxWidth: 480,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  borderRadius: 1,
                  px: 2,
                  fontStyle: "italic"
                }}>
                  {agent.description}
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: agent.status === "ready" ? "#fff" : "#333",
                      bgcolor: agent.status === "ready"
                        ? "#43a047"
                        : agent.status === "building"
                          ? "#ffb300"
                          : agent.status === "created"
                            ? "#1976d2"
                            : "#e0e0e0",
                      boxShadow: agent.status === "ready" ? 2 : 0,
                      display: "inline-block",
                      minWidth: 120,
                      textAlign: "center",
                      mr: 1,
                      textTransform: "capitalize"
                    }}>
                      {agent.status || "unverified"}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>{agent.owner}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Button
                    onClick={() => handleDelete(agent.agent_id)}
                    disabled={deleting === agent.agent_id}
                    color="error"
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: "0.85rem", px: 1.5, py: 0.5 }}
                  >
                    {deleting === agent.agent_id ? "Deleting..." : "Delete"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        component={Link}
        to="/agent/conversational"
        variant="contained"
        color="primary"
        sx={{ mt: 3, fontSize: "1rem", px: 3, py: 1 }}
      >
        Create New Agent
      </Button>
    </Box>
  );
}